import React, { useEffect } from 'react'
import useRerender from '@aytm/lib/hooks/useRerender'
import Subscription, { ISubscription } from '@aytm/lib/common/subscription'

import DevLog from '../devlog/Module'
import useViewport from '../viewport/useViewport'
import { JUMP_TO_START_OVER_JUMP_OPTION } from './components/const'

import { ModuleID } from '../types'
import { IClosePreviewButtonProps, IPreviewToolbar, IPreviewToolbarProps } from './components/types'
import {
  IPreviewModule,
  IPreviewModuleChangeQuota,
  IPreviewModuleJumpToQuestion,
  IPreviewModuleLocale,
  IPreviewOptions
} from './types'
import { UseViewportMode } from '../viewport/types'
import { SurveyMode } from '../../actions/types'

let PreviewToolbar: React.MemoExoticComponent<
  React.ForwardRefExoticComponent<IPreviewToolbarProps & React.RefAttributes<IPreviewToolbar>>
> = null
const previewToolbarProps: IPreviewToolbarProps = {
  show: true,
  collapsible: false,
  thin: false,
  controls: true
}
let previewToolbarRerender: () => void = null

let previewCloseable = false
let previewHeadless = false
let hardReset = false
let ClosePreviewButton: React.NamedExoticComponent<IClosePreviewButtonProps> = null

let jumpToQuestionOptions: IPreviewModuleJumpToQuestion[] = []
let localeOptions: IPreviewModuleLocale[] = []
let changeQuotaOptions: IPreviewModuleChangeQuota[] = []

let resetSessionURL: string = null
let qrCodeURLTemplate: string = null
let qrCodeURLTokenTemplate: string = null

export const DEFAULT_PREVIEW_OPTIONS = Object.freeze<IPreviewOptions>({
  suppressLogic: false,
  showNumbers: false,
  showHints: false,
  showLogic: false,
  showHidden: false
})
let currentPreviewOptions: IPreviewOptions = DEFAULT_PREVIEW_OPTIONS
let previewOptionsSubscription: ISubscription<IPreviewOptions, IPreviewModule> = null

const customAreaRef = React.createRef<HTMLDivElement>()

let moduleLoaded = false
const PreviewModule: IPreviewModule = {
  get id() {
    return ModuleID.PREVIEW
  },
  config({ toolbar, closeable, headless, resetURL } = {}) {
    if (null != toolbar?.show) {
      previewToolbarProps.show = toolbar.show
    }
    if (null != toolbar?.collapsible) {
      previewToolbarProps.collapsible = toolbar.collapsible
    }
    if (null != toolbar?.thin) {
      previewToolbarProps.thin = toolbar.thin
    }
    if (null != toolbar?.controls) {
      previewToolbarProps.controls = toolbar.controls
    }
    if (null != closeable) {
      previewCloseable = closeable
    }
    if (null != headless) {
      previewHeadless = headless
    }
    if (null != resetURL) {
      resetSessionURL = resetURL
      const smartURL =
        (resetURL.includes(':') ? '' : window.location.origin) + resetURL.replace('preview', 'smart_preview')
      qrCodeURLTemplate =
        smartURL + (smartURL.includes('?') ? '&' : '?') + 'mode={mode}&questionId={target}&aytm_list_token={token}'
      qrCodeURLTokenTemplate = smartURL + (smartURL.includes('?') ? '&' : '?') + 'mode={mode}&aytm_list_token={token}'
    }

    PreviewModule.updateToolbar()
    return PreviewModule
  },
  async load() {
    try {
      ;({ PreviewToolbar, ClosePreviewButton } = (await import('./components/index')).default)
      moduleLoaded = true
    } catch (error) {
      DevLog.moduleError(PreviewModule, error, 'load PreviewToolbar, ClosePreviewButton')
    }
    PreviewModule.updatePreviewOptions(loadPreviewOptions())
    return PreviewModule
  },
  get loaded() {
    return moduleLoaded
  },
  get PreviewToolbar() {
    return PreviewToolbar ? PreviewToolbarWrapper : null
  },
  get ClosePreviewButton() {
    return ClosePreviewButton
  },
  get closeable() {
    return previewCloseable
  },
  get isHeadless() {
    return previewHeadless
  },
  get resetURL() {
    return PreviewModule.loaded ? resetSessionURL : null
  },
  get hasQRCodeURL() {
    return null != qrCodeURLTemplate
  },
  qrCodeURL({ mode, target, token } = {}) {
    return mode == SurveyMode.XRAYS && qrCodeURLTemplate
      ? qrCodeURLTemplate.replace('{token}', token).replace('{mode}', mode).replace('{target}', target)
      : qrCodeURLTokenTemplate
      ? qrCodeURLTokenTemplate.replace('{mode}', mode).replace('{token}', token)
      : null
  },
  get jumpToQuestionOptions() {
    return jumpToQuestionOptions
  },
  set jumpToQuestionOptions(options) {
    jumpToQuestionOptions = [
      JUMP_TO_START_OVER_JUMP_OPTION,
      ...options.map(option => {
        return {
          ...option,
          disabled: !option.canEnter && !option.completed,
          action: option.action
            ? option.action
            : option.canEnter
            ? 'jump'
            : option.completed
            ? 'reset'
            : SurveyMode.XRAYS
        }
      })
    ]
    PreviewModule.updateToolbar()
  },
  get changeQuotaOptions() {
    return changeQuotaOptions
  },
  set changeQuotaOptions(options) {
    changeQuotaOptions = options
    // PreviewModule.updateToolbar()
  },
  get localeOptions() {
    return localeOptions
  },
  set localeOptions(options) {
    localeOptions = options
    // PreviewModule.updateToolbar()
  },
  get hardReset() {
    return hardReset
  },
  set hardReset(value) {
    hardReset = value
    // PreviewModule.updateToolbar()
  },
  get previewOptions() {
    return currentPreviewOptions
  },
  get customAreaRef() {
    return customAreaRef
  },
  get show() {
    return previewToolbarProps.show
  },
  updatePreviewOptions(previewOptions, save = false) {
    currentPreviewOptions = Object.freeze<IPreviewOptions>({
      ...currentPreviewOptions,
      ...previewOptions
    })
    DevLog._moduleLog(PreviewModule, 'updatePreviewOptions', previewOptions, currentPreviewOptions)
    if (save) {
      savePreviewOptions(currentPreviewOptions)
    }
    previewOptionsSubscription?.update(currentPreviewOptions)
    return PreviewModule
  },
  onPreviewOptionsUpdate(listener) {
    if (null == previewOptionsSubscription) {
      previewOptionsSubscription = Subscription<IPreviewOptions, IPreviewModule>()
    }
    return previewOptionsSubscription.listen(listener, PreviewModule)
  },
  updateToolbar() {
    previewToolbarRerender?.()
    return PreviewModule
  }
}

export default PreviewModule

function PreviewToolbarWrapper(props: IPreviewToolbarProps = {}) {
  const viewport = useViewport(UseViewportMode.REAL)

  const renderer = useRerender()
  previewToolbarRerender = renderer

  useEffect(() => {
    previewToolbarRerender = renderer
    return () => {
      previewToolbarRerender = null
    }
  }, [renderer])

  return !viewport.mobile ? <PreviewToolbar {...previewToolbarProps} {...props} /> : null
}

function loadPreviewOptions(): Partial<IPreviewOptions> {
  try {
    if ('undefined' !== typeof localStorage) {
      const previewOptionsJSON = localStorage.getItem('SW_previewOptions')
      if (previewOptionsJSON) {
        return JSON.parse(previewOptionsJSON) as Partial<IPreviewOptions>
      }
    }
  } catch {
    // ignore
  }
  return { showNumbers: true, showHints: true }
}

function savePreviewOptions(previewOptions: IPreviewOptions) {
  try {
    if ('undefined' !== typeof localStorage) {
      const savedPreviewOptions = { ...previewOptions }
      delete savedPreviewOptions.suppressLogic
      localStorage.setItem('SW_previewOptions', JSON.stringify(savedPreviewOptions))
    }
  } catch {
    // ignore
  }
}
